export interface PartnerCatalog {
  partner: Partner;
  id: string;
  slug?: string;
  promoCode?: string;
  title: string;
  description: string;
  template?: string;
  partnerCatalogCategories: PartnerCatalogCategory[];
  partnerCatalogUpsell: PartnerCatalogUpsell;
  catalogBlocks: PartnerCatalogBlock[];
}

export interface PartnerCatalogBlock {
  id: string;
  parentType: string;
  parentId: string;
  itemType: string;
  itemId: string;
  item: PartnerCatalogCategory | PartnerCatalogUpsell | null;
}

export interface PartnerCatalogCategory {
  id: string;
  title: string;
  description: string;
  cover: string;
  slug: string;
  catalogPageLayout: string;
  catalogOfferings?: CatalogOffering[];
  partnerCatalog?: PartnerCatalog;
  imageSrc?: string;
  catalogBlocks: PartnerCatalogBlock[];
  upsells: PartnerCatalogUpsell[];
  groupLandingPageHeaderDescription?: string;
  exploreMoreHeader?: string;
  offeringCardsHeader?: string;
}

export interface PartnerCatalogOffering {
  image: string;
  title: string;
  description: string;
  slug: string;
  multiPassOffering: boolean;
}

export interface CatalogOffering {
  image: string;
  title: string;
  description: string;
  longDescription?: string;
  slug: string;
  comingSoon: boolean;
  membership: boolean;
  targetUrl?: string;
  multiPassOffering: boolean;
  price: string;
  currency: string;
  id: string;
  partnerOfferingId: string;
  locationIDs: string[];
}

export interface PartnerCatalogUpsell {
  id: string;
  image: string;
  title: string;
  description: string;
  longDescription?: string;
  learnMoreURL: string;
  ctaLabel?: string;
  layout: "left_to_right" | "right_to_left" | "default";
}

export interface Partner {
  id: string;
  logo: string;
  name: string;
  internalName: string;
  partnerSlug: string;
  homepageURL: string;
}

export function toPartner(data: any): Partner {
  return {
    id: data.id,
    logo: data.logo,
    name: data.name,
    internalName: data.internal_name,
    partnerSlug: data.partner_slug,
    homepageURL: data.homepage_url,
  };
}

export function toPartnerCatalog(data: any): PartnerCatalog {
  return {
    partner: toPartner(data.partner),
    id: data.id,
    title: data.title,
    slug: data.slug,
    promoCode: data.promo_code,
    description: data.description,
    template: data.template,
    catalogBlocks: data?.partner_catalog_blocks?.map((b: any) =>
      toCatalogBlock(b)
    ),
    partnerCatalogCategories: data.partner_catalog_categories
      .filter((c: any) => {
        const isUseGroup =
          data.template === "flexible" || data.template === "original";
        if (isUseGroup) {
          return !!c.catalog_offerings?.length;
        }

        return true;
      })
      .map(toPartnerCatalogCategory),
    partnerCatalogUpsell:
      data.partner_catalog_upsell &&
      toPartnerCatalogUpsell(data.partner_catalog_upsell),
  };
}

export function toPartnerCatalogCategory(data: any): PartnerCatalogCategory {
  return {
    id: data.id,
    title: data.title || "",
    description: data.description || "",
    cover: data.cover_photo?.url || "",
    slug: data.slug || "",
    groupLandingPageHeaderDescription:
      data.group_landing_page_header_description,
    exploreMoreHeader: data.explore_more_header,
    catalogPageLayout: data.catalog_page_layout || "default",
    catalogOfferings: data.catalog_offerings?.map(toCatalogOffering),
    partnerCatalog: data.partner_catalog
      ? toPartnerCatalog(data.partner_catalog)
      : undefined,
    imageSrc: data.cover_photo ? data?.cover_photo : "",
    catalogBlocks: data?.partner_catalog_blocks?.map((b: any) =>
      toCatalogBlock(b)
    ),
    upsells: data?.partner_catalog_upsells?.map((u: any) =>
      toPartnerCatalogUpsell(u)
    ),
    offeringCardsHeader: data.offering_cards_header,
  };
}

function toCatalogOffering(data: any): CatalogOffering {
  return {
    image: data.image,
    title: data.title,
    description: data.description,
    slug: data.slug,
    comingSoon: data.coming_soon,
    membership: data.membership,
    targetUrl: data.target_url,
    multiPassOffering: data.multi_pass_offering, // only use for V1
    price: data.price || "",
    currency: data.currency || "",
    id: data.id || "",
    partnerOfferingId: data.partner_offering_id || "",
    longDescription: data.long_description,
    locationIDs: data.location_ids || [],
  };
}

function toPartnerCatalogUpsell(data: any): PartnerCatalogUpsell {
  return {
    id: data.id,
    image: data.image,
    title: data.title,
    description: data.description,
    learnMoreURL: data.learn_more_url,
    longDescription: data.long_description,
    layout: data.layout || "default",
    ctaLabel: data.cta_label,
  };
}

function toCatalogBlock(data: any): PartnerCatalogBlock {
  const {
    id,
    parentable_id: parentId,
    parentable_type: parentType,
    itemable_id: itemId,
    itemable_type: itemType,
    item: unionItem,
  } = data;

  let item;
  if (itemType === "PartnerCatalogCategory") {
    item = toPartnerCatalogCategory(unionItem);
  } else if (itemType === "PartnerCatalogUpsell") {
    item = toPartnerCatalogUpsell(unionItem);
  } else {
    item = null;
  }

  return {
    id,
    parentType,
    parentId,
    itemType,
    itemId,
    item,
  };
}

export function isPartnerCatalogCategory(
  item: PartnerCatalogCategory | PartnerCatalogUpsell | null
): item is PartnerCatalogCategory {
  return (
    item !== null &&
    "slug" in item &&
    "catalogOfferings" in item &&
    "catalogBlocks" in item
  );
}
